import React from 'react';
import styled, {withTheme} from 'styled-components';
import {withRouter} from 'react-router-dom';
import parseHtml from 'html-react-parser';
import LayoutContainer from '../../components/LayoutContainer';
import Title from '../../components/Title';
import Box from '../../components/Box';
import Button from '../../components/Button';
import MainContainer from '../../components/MainContainer';
import Logo from '../../components/Logo';
import DisplayIban from '../../components/DisplayIban';
import ProgressBar from '../../components/ProgressBar';
import IBAN from 'iban';
import PaymentMethodButtonContainer from '../../components/PaymentMethodButtonContainer';
import Checkbox from '../../components/Checkbox';
import ModalDialog from '../../components/ModalDialog';
import _ from 'lodash';
import parseText from '../../utils/parseText';
import handleContentAction from '../../utils/handleContentAction';
import {renderDialog, closeDialog, save} from '../../reducer';
import {gaPaymentFailure} from '../../googleAnalytics/paymentFailure';
import {Field, Form, FormValidation} from '@nexios/frontend-forms';
import {isSepaCountryIban} from '@nexios/validation';
import {connect} from 'react-redux';
import ErrorMessage from '../../components/ErrorMessage';
import PaymentFailedMessage from '../../components/PaymentFailedMessage';
import FieldWrapper from '../../components/FieldWrapper';
import Backbutton from '../../components/Backbutton';
import Dropdown from '../../components/Dropdown';
import NewsletterOptIn from '../../components/NewsletterOptIn';
import {optInChoices, normalizeOptIns} from '../../utils/optIn';
import Input from '../../components/Input';
import {Helmet} from 'react-helmet';
import parseAndGetBigNumber from '../../utils/parseAndGetBigNumber';
import parseAuthorizeLabel from '../../utils/parsePrivacyStatement';
import useScrollToTop from '../../hooks/useScrollToTop';
import Teaser from '../../components/Teaser';
import ProductImage from '../../components/ProductImage';
import CollectingSideBar from '../../components/CollectingSideBar';
import OrderSummaryText from '../../components/OrderSummaryText';
import TextToHtml from '../../components/TextToHtml';
import PrivacyLabel from '../../components/PrivacyLabel';
import DonationBoxSideBar from '../../components/DonationBoxSideBar';
import DJSBSideBar from '../../components/DJSBSideBar';
import CollectingImage from '../../components/CollectingImage';

const parseDirectDebitText = (ctx, {mandateType, directDebitOneOff, directDebitOther, directDebitTemporary}) => {
  const notOneOffText = mandateType === 'temporary' ? directDebitTemporary : directDebitOther;
  const txt = ctx.frequency === 'oneOff' ? directDebitOneOff : notOneOffText;
  return parseText(ctx, txt);
};

const MandateTextStyled = styled.div`
  font-size: ${props => props.theme?.mandateText?.fontSize};
  color: ${props => props.theme?.mandateText?.color};
  font-style: ${props => props.theme?.mandateText?.fontStyle};
`;

const Step3 = ({
  formContext,
  dmBoxInfo,
  collectorInfo,
  isDialogVisible,
  renderDialog,
  closeDialog,
  save,
  history,
  gaPaymentFailure,
  previous,
  progressBar,
  isProductForm,
  theme
}) => {
  useScrollToTop();
  const formConfig = {
    onSubmit: () => {
      save(history);
    },
    onDataChanged: (ctx, callback) => {
      normalizeOptIns(ctx);
      return callback();
    },
    onInit: (ctx) => {
      ctx.setDefaultValues({
        donateAsOrganisation: false,
        optInAdminCostContribution: ctx.optInAdminCostContribution === true && ctx.frequency === 'oneOff'
      });

      ctx.setList('optInChoices', optInChoices);

      // check if payment failed and we need to inform GA
      gaPaymentFailure();

      if (!isProductForm) {
        ctx.setList('frequencies', formContext.frequencies);
        const frequency = formContext.frequencies.find(f => f.value === ctx.frequency);
        ctx.setList('amounts', frequency.amounts.map(a => ({
          label: a.name,
          value: a.amount_m.toString()
        })));

        if (ctx.frequency === 'oneOff') {
          ctx.setValues({
            paymentMethod: formContext.paymentMethods[0].value
          });
        }

        if (ctx.frequency !== 'oneOff') {
          ctx.setValues({
            paymentMethod: 'directDebit'
          });
        }
      }

      ctx.setList('idealBanks', formContext.idealBanks);
      ctx.setList('creditCardBanks', formContext.creditCardBanks);
      ctx.setList('paymentMethods', formContext.paymentMethods);

      // set amountText on top of widget
      if (ctx.frequency === 'oneOff') {
        ctx.setValues({
          amountText: parseText(ctx, `Ik geef ${theme?.amountText?.boldFrequency ? '<strong>' : ''}eenmalig${theme?.amountText?.boldFrequency ? '</strong>' : ''} €{{donationAmount}} ${ctx.optInAdminCostContribution === true ? '+ €{{adminCostContribution}} administratiekosten' : ''}`)
        });
      } else if (formContext.mandateType === 'temporary') {
        ctx.setValues({
          amountText: parseText(ctx, `Ik geef {{period}} lang, elke ${theme?.amountText?.boldFrequency ? '<strong>' : ''}{{frequency}}${theme?.amountText?.boldFrequency ? '</strong>' : ''} €{{amount}}`)
        });
      } else {
        ctx.setValues({
          amountText: parseText(ctx, `Ik geef €{{amount}} per ${theme?.amountText?.boldFrequency ? '<strong>' : ''}{{frequency}}${theme?.amountText?.boldFrequency ? '</strong>' : ''}`)
        });
      }

      const authorizeLabel = ctx.donateAsOrganisation ? formContext.privacyConfirmationTextOrganisation : formContext.privacyConfirmationTextPerson;
      const directDebitText = parseDirectDebitText(ctx, formContext);

      ctx.setValues({
        step3Header: parseText(ctx, _.get(formContext, ['step3Header'])),
        authorizeLabel,
        directDebitText
      });

      if (formContext.step3BodyText) {
        ctx.setValues({step3BodyText: parseText(ctx, formContext.step3BodyText)});
      }

      if (!formContext.authorizeAsCheckbox) {
        ctx.setValues({
          authorize: true
        });
      }

      let orderSummary;
      const productAmount = _.toInteger(ctx.productAmount);
      const formattedAmount = parseAndGetBigNumber(ctx.amount_m || 0).toFormat(2);
      if (productAmount > 1) {
        orderSummary = `${productAmount} ${formContext.productNamePlural}: € ${formattedAmount}`;
      } else {
        orderSummary = `1 ${formContext.productName}: € ${formattedAmount}`;
      }

      ctx.setValues({
        orderSummary: orderSummary
      });

      if (formContext.newsletterOptIn === 'dropdown') {
        const newsletterDefault = _.get(formContext, 'newsletterDefault');
        if (newsletterDefault === true || newsletterDefault === false) {
          ctx.setDefaultValues({
            allowNewsletter: newsletterDefault
          });
        }
      } else if (_.get(formContext, 'newsletterDefault', theme.allowNewsletterDefault) === true) {
        ctx.setDefaultValues({
          allowNewsletter: true
        });
      }

    },
    fields: {
      paymentMethod: {
        isVisible: ctx => ctx.frequency === 'oneOff' || isProductForm,
        rules: [
          {
            isValid: ctx => !_.isNil(ctx.paymentMethod),
            message: theme.showRequiredText && 'Betaalwijze is niet ingevuld'
          }
        ]
      },
      directDebitText: {
        isVisible: ctx => ctx.paymentMethod === 'directDebit'
      },
      iban: {
        isVisible: ctx => ctx.paymentMethod === 'directDebit',
        isRequired: ctx => ctx.paymentMethod === 'directDebit',
        rules: [
          {
            isValid: ctx => !_.isNil(ctx.iban) && ctx.iban !== '',
            message: theme.showRequiredText && 'Rekeningnummer is niet ingevuld'
          },
          {
            message: 'Vul hier een geldig IBAN nummer in.',
            isValid: ctx => {
              var value = ctx.iban;

              if (_.isNil(value)) {
                return true;
              }

              return IBAN.isValid(value);
            }
          },
          {
            message: 'IBAN moet afkomstig zijn uit een SEPA land',
            isValid: ctx => {
              const value = ctx.iban;
              if (_.isNil(value) || value === '') return true;

              return isSepaCountryIban(value);
            }
          }
        ]
      },
      bank: {
        isVisible: ctx => ctx.paymentMethod === 'ideal' && !formContext.hideBankOptions,
        isRequired: ctx => ctx.paymentMethod === 'ideal' && !formContext.hideBankOptions,
        rules: [
          {
            isValid: ctx => !_.isNil(ctx.bank) && ctx.bank !== '',
            message: theme.showRequiredText && 'Bank is niet ingevuld'
          }
        ]
      },
      creditCardBank: {
        isVisible: ctx => ctx.paymentMethod === 'creditCard',
        isRequired: ctx => ctx.paymentMethod === 'creditCard',
        rules: [
          {
            isValid: ctx => !_.isNil(ctx.creditCardBank) && ctx.creditCardBank !== '',
            message: theme.showRequiredText && 'Creditcard is niet ingevuld'
          }
        ]
      },
      authorize: {
        isVisible: ctx => ctx.donationPageType !== '12_donationAnon',
        isRequired: ctx => ctx.donationPageType !== '12_donationAnon',
        rules: [
          {
            isValid: ctx => ctx.authorize === true,
            message: theme.showRequiredText && 'Dit veld is vereist'
          }
        ]
      },
      allowNewsletter: {
        isVisible: () => formContext.allowNewsletter === true && formContext.allowNewsletterDisplay === 'step3' && formContext.donationPageType !== '12_donationAnon'
      },
      amountText: {
        isVisible: () => !isProductForm && theme.step3HideAmountText !== true
      },
      orderSummary: {
        isVisible: () => isProductForm
      }
    }
  };

  let themeHeader = formContext.donationPageType === '12_donationAnon' ? theme.step3WidgetHeaderTextNoNaw : theme.step3WidgetHeaderText;
  let paymentLabelText;
  if (theme?.paymentMethodsContainer?.labelText) {
    paymentLabelText = <p style={{...theme.paymentMethodsContainer.labelStyle}}>{theme?.paymentMethodsContainer?.labelText}</p>;
  }

  let content = <Box position={formContext.textPosition}
    hasTeaser={!theme.teaser.hideOnStep3 && (!_.isNil(formContext.pageTeaserHeader) || !_.isNil(formContext.pageTeaserBodyText) || !_.isEmpty(formContext.pageTeaserCarouselItems))}>
    <Title title={themeHeader || formContext.step3Header} showLogo={theme.title?.showLogoStep3}><ProgressBar progress={progressBar} /></Title>
    <Form
      formInstanceKey='donate'
      formContext={formContext}
      formConfig={formConfig}>
      <PaymentFailedMessage formContext={formContext} />
      {!theme?.error?.showBelowBodyText && <FormValidation
        component={ErrorMessage}
        errorMessageTemplate={formContext.errorMessageTemplate}
        formContext={formContext}
        errorMessage='Je hebt nog niet alle velden ingevuld.' />}
      <div className='widgetContent'>
        <div onClick={handleContentAction({history})}>
          <Field
            component={({value}) => <TextToHtml text={value} />}
            name='step3BodyText' />
        </div>
        {theme?.error?.showBelowBodyText && <FormValidation
          component={ErrorMessage}
          errorMessageTemplate={formContext.errorMessageTemplate}
          formContext={formContext}
          errorMessage='Je hebt nog niet alle velden ingevuld.' />}
        <Field
          component={({value}) => <div className='marginBottom'>{parseHtml(value || '')}</div>}
          name='amountText' />
        <Field
          name='orderSummary'
          component={({value}) => <OrderSummaryText orderText={value} />}
        />
        <Field
          name='paymentMethod'
          component={(props) => {
            return <div className='marginBottom'>{paymentLabelText}<PaymentMethodButtonContainer
              fieldName='paymentMethod'
              valueName='paymentMethod'
              list={props.list} /></div>;
          }}
          list='paymentMethods'
        />
        <Field
          list='idealBanks'
          name='bank'
          component={(props) => <div className='marginBottom'><FieldWrapper {...props} /></div>}
          placeholder={'Kies je bank'}
          inputComponent={Dropdown}
        />
        <Field
          list='creditCardBanks'
          name='creditCardBank'
          component={(props) => <div className='marginBottom'><FieldWrapper {...props} /></div>}
          placeholder={'Kies je creditcard'}
          inputComponent={Dropdown}
        />
        <Field
          label='Rekeningnummer'
          name='iban'
          component={(props) => <div className='marginBottom'><FieldWrapper {...props} /></div>}
          inputComponent={Input}
          placeholder='NL11ABNA012345678'
          forceUppercase />
        <Field
          component={({value}) => <MandateTextStyled className='marginBottom'>{value}</MandateTextStyled>}
          name='directDebitText' />
        <div className='marginBottom'>
          {formContext.authorizeAsCheckbox && <Field
            hideLabel
            largeValidationGap
            name='authorize'
            component={FieldWrapper}
            inputComponent={Checkbox}
            label={<Field
              name='authorizeLabel'
              component={({value}) =>
                <PrivacyLabel page='step3' text={parseAuthorizeLabel(value, renderDialog)} />} />} />}

          {!formContext.authorizeAsCheckbox && <Field
            name='authorizeLabel'
            component={({value}) =>
              <PrivacyLabel page='step3' text={parseAuthorizeLabel(value, renderDialog)} />} />}
        </div>
        <div className='marginBottom'>
          <NewsletterOptIn label={formContext.newsletterText || 'Houd mij op de hoogte over nieuwe ontwikkelingen.'} />
        </div>
        <div className='marginBottom'>
          <Field
            name='paymentMethod'
            noAutoHide={true}
            component={({value}) => <FormValidation
              component={Button}
              label={value === 'directDebit' ? formContext.paymentMethodMandateCallToActionLabel || 'Doneer nu' : formContext.paymentMethodCallToActionLabel || 'Naar betalen'}
              type='submit'
            />} />
        </div>
        <div>
          <Backbutton label={formContext.backButtonLabel} link={`${previous}${history.location.search}`} />
        </div>
      </div>
    </Form>
  </Box>;

  if (isDialogVisible) {
    content = <ModalDialog close={() => {
      closeDialog();
    }}>
      <div dangerouslySetInnerHTML={{__html: formContext.privacyStatement}} />
    </ModalDialog>;
  }

  return <>
    <Helmet>
      <title>{(formContext.isDonationBox && dmBoxInfo?.boxHeader_txt) || formContext.paymentMethodBrowserTitle}</title>
    </Helmet>
    <MainContainer
      backgroundImageUrl={theme?.donationBox?.imageAsBackground && formContext.isDonationBox && dmBoxInfo?.image?.publicUrl
        ? dmBoxInfo.image.publicUrl
        : formContext.backgroundImageUrl}
      backgroundImageTabletUrl={theme?.donationBox?.imageAsBackground && formContext.isDonationBox && dmBoxInfo?.image?.publicUrl
        ? dmBoxInfo.image.publicUrl
        : formContext.backgroundImageTabletUrl}
      backgroundImageMobileUrl={theme?.donationBox?.imageAsBackground && formContext.isDonationBox && dmBoxInfo?.image?.publicUrl
        ? dmBoxInfo.image.publicUrl
        : formContext.backgroundImageMobileUrl}
    />
    <LayoutContainer
      showCollectingWidget={formContext.showCollectingWidget || formContext.isDonationBox}
      position={formContext.textPosition}
      header={<div>
        <div style={{float: 'left', display: 'inline-block'}}>
          <Logo url={formContext.websiteUrl} />
        </div>
        {formContext.isCollecting && theme?.collecting?.imageTopRight &&
          <div style={{float: 'right', display: 'inline-block'}}><CollectingImage collectorInfo={collectorInfo} /></div>}
        <DisplayIban iban='NL08 INGB 0000 0005 55' />
      </div>}
      content={content}
      sidebar={
        <>
          {theme?.djsb?.showDJSB && <DJSBSideBar formContext={formContext}/>}
          {isProductForm && <ProductImage url={_.get(formContext, ['productImageUrl'])} collapseIfEmpty />}
          {formContext.showCollectingWidget
            ? <CollectingSideBar collectorInfo={collectorInfo}><Teaser header={formContext.pageTeaserHeader} body={formContext.pageTeaserBodyText} formContext={formContext} /></CollectingSideBar>
            : formContext.isDonationBox
              ? <DonationBoxSideBar dmBoxInfo={dmBoxInfo} header={formContext.pageTeaserHeader} body={formContext.pageTeaserBodyText}>
                  <Teaser header={formContext.pageTeaserHeader} body={formContext.pageTeaserBodyText} formContext={formContext} /></DonationBoxSideBar>
              : !theme.teaser.hideOnStep3 && <Teaser header={formContext.pageTeaserHeader} body={formContext.pageTeaserBodyText} formContext={formContext} />}
        </>}
      footer={formContext.footer}
      formContext={formContext}
      step={'3'}
    />
  </>;
};

const mapStateToProps = (state) => {
  return {
    isDialogVisible: state.reducer.isDialogVisible,
    formContext: state.reducer.formContext,
    isProductForm: state.reducer.isProductForm
  };
};

const mapDispatchToProps = (dispatch) => ({
  save: (history) => {
    dispatch(save(history));
  },
  gaPaymentFailure: () => {
    dispatch(gaPaymentFailure());
  },
  renderDialog: (e) => {
    e.stopPropagation();
    e.preventDefault();
    dispatch(renderDialog());
  },
  closeDialog: () => {
    dispatch(closeDialog());
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(withTheme(Step3)));
